// frontend/src/services/apiClient.js
import axios from 'axios';
import { isTokenExpiringSoon, isTokenExpired } from '../utils/tokenUtils';

const API_URL = process.env.REACT_APP_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:5001';

const apiClient = axios.create({
    baseURL: API_URL,
    timeout: 10000, // 10 second timeout for requests
});

// Track if we're currently refreshing to prevent duplicate requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

// Helper function to refresh the token
const refreshAuthToken = async () => {
    try {
        const currentToken = localStorage.getItem('token');
        
        if (!currentToken) {
            throw new Error('No token to refresh');
        }

        // If token is already expired, don't try to refresh
        if (isTokenExpired(currentToken)) {
            throw new Error('Token already expired');
        }

        console.log('🔄 Refreshing token...');
        
        const response = await axios.post(
            `${API_URL}/api/auth/refresh`,
            {},
            {
                headers: {
                    'Authorization': `Bearer ${currentToken}`
                }
            }
        );

        const { token: newToken, user } = response.data;

        // Update localStorage and broadcast to other tabs
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Broadcast token update to other tabs
        window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
            detail: { token: newToken, user } 
        }));

        console.log('✅ Token refreshed successfully');
        
        return newToken;

    } catch (error) {
        console.error('❌ Token refresh failed:', error.response?.data || error.message);
        
        // Check if re-authentication is required
        if (error.response?.data?.requiresReauth) {
            // Clear storage and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        
        throw error;
    }
};

// Add a request interceptor to include the token and check if refresh is needed
apiClient.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('token');
        
        if (token) {
            // Check if token is expiring soon (< 1 hour) and refresh if needed
            if (isTokenExpiringSoon(token, 60)) {
                console.log('⚠️ Token expiring soon, refreshing before request...');
                
                if (isRefreshing) {
                    // Already refreshing, wait for it to complete
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(newToken => {
                        config.headers['Authorization'] = `Bearer ${newToken}`;
                        return config;
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                isRefreshing = true;

                try {
                    const newToken = await refreshAuthToken();
                    processQueue(null, newToken);
                    config.headers['Authorization'] = `Bearer ${newToken}`;
                } catch (error) {
                    processQueue(error, null);
                    return Promise.reject(error);
                } finally {
                    isRefreshing = false;
                }
            } else {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle auth errors
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            
            // Check if it's a re-auth required error
            if (error.response?.data?.requiresReauth) {
                console.log('🔐 Re-authentication required');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }

            // If we're already refreshing, wait for it
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return apiClient(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newToken = await refreshAuthToken();
                processQueue(null, newToken);
                
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                
                // Token refresh failed - clear storage and redirect
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Listen for token refresh events from other tabs
if (typeof window !== 'undefined') {
    window.addEventListener('tokenRefreshed', (event) => {
        const { token, user } = event.detail;
        console.log('📡 Token updated from another tab');
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
    });

    // Listen for storage events (when localStorage is updated in another tab)
    window.addEventListener('storage', (event) => {
        if (event.key === 'token' && !event.newValue) {
            // Token was removed in another tab - log out this tab too
            console.log('🚪 Logged out from another tab');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
    });
}

export default apiClient;